<template>
  <div class="mt-6 px-4 flex justify-between items-center w-full">
    <div>
      <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
        {{ t('aiTools.title') }}
      </h1>
      <h1 class="text text-gray-900 dark:text-gray-200">
        {{ t('aiTools.subtitle') }}
      </h1>
    </div>

    <div class="flex items-center space-x-4 rtl:space-x-reverse">
      <label class="input input-bordered flex items-center" style="width:330px">
        <input type="text" v-model="searchValue" class="grow" :placeholder="t('common.search')" />
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
          <path fill-rule="evenodd"
            d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
            clip-rule="evenodd" />
        </svg>
      </label>
      
      <button class="btn" @click="handleCreateClick">{{ t('aiTools.createNewTool') }}</button>
    </div>
  </div>

  <div class="mt-6 px-4">
    <ThemeOnly theme="modern" :invert="true">
      <div class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex"
        style="border-radius: 10px; height: 70vh;">
        <div class="w-full overflow-y-auto">
          <details
            v-for="group in groupedTools"
            :key="group.collectionId"
            class="mb-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
            :open="group.collectionId === 'custom'"
          >
            <summary class="cursor-pointer list-none">
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-3 min-w-0">
                  <Icon v-if="getCollectionIcon(group.collectionId) !== ''" :icon="getCollectionIcon(group.collectionId)" class="w-7 h-7 rounded-md bg-base-200 p-1 text-base-content" />
                  <img v-else :src="group.logo" alt="" class="w-7 h-7 object-contain rounded-md bg-base-200 p-1" />
                  <div class="min-w-0">
                    <div class="font-semibold text-gray-900 dark:text-gray-200 truncate">{{ group.name }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ group.items.length }} tools</div>
                  </div>
                </div>
                <Icon icon="tabler:chevron-down" class="w-5 h-5 text-gray-500" />
              </div>
            </summary>

            <div class="mt-3">
              <EasyDataTable
                :headers="headers"
                :items="group.items"
                buttons-pagination
              >
                <template #item-toolData.requiredParams="item">
                  {{ formatRequiredParams(item) }}
                </template>

                <template #item-createdAt="item">
                  {{ formatDateTime(item !== null && item !== undefined ? item.createdAt : undefined) }}
                </template>

                <template #item-toolData.description="item">
                  {{ formatToolDescription(item) }}
                </template>

                <template #item-toolData.executionMode="item">
                  {{ formatExecutionMode(item) }}
                </template>

                <template #item-toolName="item">
                  <div class="flex items-center gap-2 min-w-0">
                    <Icon v-if="getToolIcon(item) !== ''" :icon="getToolIcon(item)" class="w-5 h-5 rounded bg-base-200 p-0.5 text-base-content" />
                    <img v-else :src="getToolLogo(item)" alt="" class="w-5 h-5 object-contain rounded bg-base-200 p-0.5" />
                    <span class="truncate">{{ item.toolName }}</span>
                  </div>
                </template>

                <template #item-options="item">
                  <button
                    @click="handleEditClick(item.toolId)"
                    class="mr-4 hover:text-red-900 mr-2 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    :disabled="isBuiltInTool(item) === true"
                  >
                    <Icon v-if="isBuiltInTool(item) === true" icon="tabler:lock" class="w-4 h-4 mr-1" />
                    {{ t('common.edit') }}
                  </button>
                  <button
                    @click="handleDeleteClick(item.toolId)"
                    class="hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    :disabled="isSystemToolRow(item) === true"
                  >
                    {{ t('common.delete') }}
                  </button>
                </template>

                <template #loading>
                  <div class="flex justify-center items-center p-8">
                    <ModernLoader />
                  </div>
                </template>
              </EasyDataTable>
            </div>
          </details>
        </div>
      </div>
    </ThemeOnly>

    <ThemeOnly theme="modern">
      <div class="space-y-3">
        <details
          v-for="group in groupedTools"
          :key="'modern-' + group.collectionId"
          class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
          :open="group.collectionId === 'custom'"
        >
          <summary class="cursor-pointer list-none">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <Icon v-if="getCollectionIcon(group.collectionId) !== ''" :icon="getCollectionIcon(group.collectionId)" class="w-8 h-8 rounded-xl bg-base-200 p-1 text-base-content" />
                <img v-else :src="group.logo" alt="" class="w-8 h-8 object-contain rounded-xl bg-base-200 p-1" />
                <div class="min-w-0">
                  <div class="font-semibold text-gray-900 dark:text-gray-200 truncate">{{ group.name }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ group.items.length }} tools</div>
                </div>
              </div>
              <Icon icon="tabler:chevron-down" class="w-5 h-5 text-gray-500" />
            </div>
          </summary>

          <div class="mt-4">
            <ModernAgentTable
              :headers="headers"
              :items="group.items"
              :loading="isLoading === true"
            >
              <template #item-toolData.requiredParams="{ item }">
                {{ formatRequiredParams(item) }}
              </template>

              <template #item-createdAt="{ item }">
                {{ formatDateTime(item.createdAt) }}
              </template>

              <template #item-toolData.description="{ item }">
                {{ formatToolDescription(item) }}
              </template>

              <template #item-toolData.executionMode="{ item }">
                {{ formatExecutionMode(item) }}
              </template>

              <template #item-toolName="{ item }">
                <div class="flex items-center gap-2 min-w-0">
                  <Icon v-if="getToolIcon(item) !== ''" :icon="getToolIcon(item)" class="w-5 h-5 rounded bg-base-200 p-0.5 text-base-content" />
                  <img v-else :src="getToolLogo(item)" alt="" class="w-5 h-5 object-contain rounded bg-base-200 p-0.5" />
                  <span class="truncate">{{ item.toolName }}</span>
                </div>
              </template>

              <template #item-options="{ item }">
                <div class="flex items-center justify-end space-x-2">
                  <button @click="handleEditClick(item.toolId)" class="action-btn" :disabled="isBuiltInTool(item) === true">
                    <Icon :icon="isBuiltInTool(item) === true ? 'tabler:lock' : 'tabler:edit'" width="16" />
                    <span class="btn-text">{{ t('common.edit') }}</span>
                  </button>
                  <button @click="handleDeleteClick(item.toolId)" class="action-btn btn-delete" :disabled="isSystemToolRow(item) === true">
                    <Icon icon="tabler:trash" width="16" />
                    <span class="btn-text">{{ t('common.delete') }}</span>
                  </button>
                </div>
              </template>
            </ModernAgentTable>
          </div>
        </details>
      </div>
    </ThemeOnly>
  </div>

  <div class="modal" :class="{ 'modal-open': showCreateModal === true }">
    <div class="modal-box w-11/12 max-w-5xl overflow-y-auto max-h-[90vh] relative">
      <div class="sticky top-0 bg-white dark:bg-gray-800 z-10 pb-4">
        <h3 class="font-bold text-lg">{{ isEditMode === true ? t('aiTools.editTool') : t('aiTools.createNewTool') }}</h3>
      </div>
      
      <div class="space-y-4">
        <div>
          <label class="label">
            <span class="label-text">{{ t('aiTools.toolName') }}</span>
          </label>
          <input type="text" v-model="currentTool.toolName" @input="handleToolNameInput" class="input input-bordered w-full" :placeholder="t('aiTools.toolNamePlaceholder')" />
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ t('aiTools.executionMode') }}</span>
          </label>
          <select v-model="currentTool.toolData.executionMode" class="select select-bordered w-full">
            <option value="sync">{{ t('aiTools.executionModeSync') }}</option>
            <option value="async">{{ t('aiTools.executionModeAsync') }}</option>
          </select>
          <div class="text-sm text-gray-500 mt-2">
            {{ currentTool.toolData.executionMode === 'async' ? t('aiTools.executionModeAsyncHelp') : t('aiTools.executionModeSyncHelp') }}
          </div>
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ t('aiTools.functionConfig') }}</span>
          </label>
          <div class="text-sm text-gray-500 mb-2">
            {{ t('aiTools.functionConfigHelp') }}
          </div>
          <div class="w-full">
            <div ref="monacoContainer" class="monaco-editor-container" style="height: 300px;"></div>
          </div>
          <div class="mt-2 border rounded-md p-3 bg-base-100">
            <label class="label">
              <span class="label-text">{{ t('aiTools.aiPromptTitle') }}</span>
            </label>
            <div class="text-sm text-gray-500 mb-2">
              {{ t('aiTools.aiPromptHelp') }}
            </div>
            <textarea
              v-model="aiPromptInput"
              class="textarea textarea-bordered w-full"
              style="min-height: 90px;"
              :placeholder="t('aiTools.aiPromptPlaceholder')"
            ></textarea>
            <div class="mt-2 flex items-center justify-between gap-2">
              <div class="text-xs text-gray-500 truncate">{{ aiPromptStatus }}</div>
              <button
                @click="generateToolFromPrompt"
                class="btn btn-sm"
                :disabled="isGeneratingToolWithAi === true"
              >
                {{ isGeneratingToolWithAi === true ? t('aiTools.aiPromptSending') : t('aiTools.aiPromptSend') }}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ t('aiTools.requiredParams') }}</span>
          </label>
          <div v-for="(param, index) in currentTool.toolData.requiredParams" :key="index" class="flex mb-2">
            <input 
              type="text" 
              v-model="currentTool.toolData.requiredParams[index]" 
              class="input input-bordered flex-1 mr-2" 
              :placeholder="t('aiTools.paramNamePlaceholder')"
            />
            <button @click="removeRequiredParam(index)" class="btn btn-sm btn-error">{{ t('aiTools.remove') }}</button>
          </div>
          <button @click="addRequiredParam" class="btn btn-sm mt-2">{{ t('aiTools.addParam') }}</button>
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ t('aiTools.functionDescription') }}</span>
          </label>
          <textarea
            v-model="currentTool.toolData.description"
            class="textarea textarea-bordered w-full"
            style="min-height: 80px;"
            :placeholder="t('aiTools.functionDescriptionPlaceholder')"
          ></textarea>
        </div>

        <div>
          <label class="label">
            <span class="label-text">{{ t('aiTools.exampleUsage') }}</span>
          </label>
          <textarea
            v-model="currentTool.toolData.example"
            class="textarea textarea-bordered w-full"
            style="min-height: 80px;"
            :placeholder="t('aiTools.exampleUsagePlaceholder')"
          ></textarea>
        </div>

        <div class="mt-4">
          <button @click="validateFunction" class="btn">{{ t('aiTools.validateFunction') }}</button>
          <span v-if="validationError !== null" class="text-error text-sm ml-2">{{ validationError }}</span>
        </div>

        <div class="mt-4 border rounded-md p-3">
          <label class="label">
            <span class="label-text">{{ t('aiTools.fakeValuesTitle') }}</span>
          </label>
          <div class="text-sm text-gray-500 mb-2">
            {{ t('aiTools.fakeValuesHelp') }}
          </div>
          <textarea
            v-model="fakeValuesInput"
            class="textarea textarea-bordered w-full font-mono"
            style="min-height: 120px;"
            :placeholder="t('aiTools.fakeValuesPlaceholder')"
          ></textarea>

          <div class="flex items-center justify-between mt-2">
            <label class="label cursor-pointer justify-start gap-2">
              <input type="checkbox" class="checkbox checkbox-sm" v-model="autoFillMissingFakeValues" />
              <span class="label-text text-sm">{{ t('aiTools.autoFillMissingFakeValues') }}</span>
            </label>
            <button @click="fillFakeValuesTemplate" class="btn btn-sm">{{ t('aiTools.fillFakeValues') }}</button>
          </div>

          <div class="mt-3">
            <button @click="runToolWithFakeValues" class="btn btn-success" :disabled="isRunningTool === true">
              {{ isRunningTool === true ? t('aiTools.runningTool') : t('aiTools.runWithFakeValues') }}
            </button>
          </div>

          <div v-if="runError !== ''" class="text-error text-sm mt-2">{{ runError }}</div>

          <div v-if="runResult !== '' || runParamsPreview !== '' || runStatus !== ''" class="mt-3">
            <div v-if="runStatus !== ''" class="text-sm mt-2">{{ runStatus }}</div>
            <label class="label">
              <span class="label-text">{{ t('aiTools.runOutput') }}</span>
            </label>
            <pre class="bg-base-200 p-3 rounded text-sm whitespace-pre-wrap">{{ runResult !== '' ? runResult : t('aiTools.emptyRunResult') }}</pre>

            <div v-if="runParamsPreview !== ''" class="mt-2">
              <label class="label">
                <span class="label-text">{{ t('aiTools.paramsUsedLabel') }}</span>
              </label>
              <pre class="bg-base-200 p-3 rounded text-sm whitespace-pre-wrap">{{ runParamsPreview }}</pre>
            </div>
          </div>
        </div>
      </div>

      <div class="sticky bottom-0 bg-white dark:bg-gray-800 pt-4 pb-2 modal-action">
        <button class="btn" @click="closeModal">{{ t('common.cancel') }}</button>
        <button class="btn" style="margin-right: 1%;" @click="saveTool">{{ t('common.save') }}</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="closeModal">close</button>
    </form>
  </div>

  <it-modal v-model="isDeleteModalOpen">
    <div class="p-4">
      <h2 class="text-2xl font-bold mb-4">{{ t('aiTools.deleteModalTitle') }}</h2>
      {{ t('aiTools.deleteModalText') }}

      <div class="flex justify-end mt-6">
        <button @click="isDeleteModalOpen=false" type="button mr-2" class="mr-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          {{ t('common.cancel') }}
        </button>
        <button @click="deleteToolConfirmed()" type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('common.delete') }}
        </button>
      </div>
    </div>
  </it-modal>
</template>

<script setup>

import { ref, onMounted, nextTick, watch, onUnmounted, computed } from 'vue';
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';
import { useToast } from 'vue-toastification';
import EasyDataTable from "vue3-easy-data-table";
import loader from '@monaco-editor/loader';
import hljs from 'highlight.js';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { Icon } from '@iconify/vue/dist/offline';
import ModernLoader from '@/components/ModernLoader.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import ModernAgentTable from '@/components/ModernAgentTable.vue';
import { getToolCollectionMeta, getToolLogoUrl } from '@/shared/tools/toolCollections.js';

const { t } = useI18n();
const apiEndpoint = computed(() => globalState.apiEndpoint);
const tools = ref([]);
const isLoading = ref(false);
const showCreateModal = ref(false);
const isEditMode = ref(false);
const validationError = ref(null);
const searchFields = ["toolId", "toolName", "toolData.description"];
const searchValue = ref("");
let resizeTimeout = null;
const isDeleteModalOpen = ref(false);
const selectedToolId = ref(null);
const fakeValuesInput = ref('{}');
const autoFillMissingFakeValues = ref(true);
const isRunningTool = ref(false);
const runResult = ref('');
const runError = ref('');
const runParamsPreview = ref('');
const runStatus = ref('');
const aiPromptInput = ref('');
const isGeneratingToolWithAi = ref(false);
const aiPromptStatus = ref('');

let monacoEditorInstance = null;
const monacoContainer = ref(null);

const headers = computed(() => [
  { text: t('aiTools.headers.toolId'), value: "toolId", sortable: true },
  { text: t('aiTools.headers.toolName'), value: "toolName", sortable: true },
  { text: t('aiTools.headers.description'), value: "toolData.description", sortable: true },
  { text: t('aiTools.headers.executionMode'), value: "toolData.executionMode", sortable: true },
  { text: t('aiTools.headers.requiredParams'), value: "toolData.requiredParams", sortable: true },
  { text: t('aiTools.headers.createdAt'), value: "createdAt", sortable: true },
  { text: t('aiTools.headers.options'), value: "options" }
]);

const toast = useToast();
const initialFunctionConfig = `const { firstParam, secondParam } = params;






return {
  result: "Replace with your logic"
};`;

const currentTool = ref({
  toolId: null,
  toolGuid: null,
  organizationId: 0,
  toolName: '',
  toolData: {
    functionConfig: initialFunctionConfig,
    description: '',
    example: '',
    executionMode: 'sync',
    requiredParams: []
  }
});

const sanitizeToolName = (value) => {
  const rawValue = typeof value === 'string' ? value : '';
  return rawValue.replace(/[^A-Za-z0-9_]/g, '');
};

const handleToolNameInput = () => {
  const sanitizedToolName = sanitizeToolName(currentTool.value.toolName);
  if (currentTool.value.toolName !== sanitizedToolName) {
    currentTool.value.toolName = sanitizedToolName;
  }
};

const normalizeToolRow = (row) => {
  const safeRow = row !== null && typeof row === 'object' ? row : {};
  const toolDataRaw = safeRow.toolData !== null && typeof safeRow.toolData === 'object'
    ? safeRow.toolData
    : (safeRow.tooldata !== null && typeof safeRow.tooldata === 'object' ? safeRow.tooldata : {});
  const requiredParamsRaw = Array.isArray(toolDataRaw.requiredParams) === true ? toolDataRaw.requiredParams : [];
  const descriptionRaw = typeof toolDataRaw.description === 'string' ? toolDataRaw.description : '';
  const exampleRaw = typeof toolDataRaw.example === 'string' ? toolDataRaw.example : '';
  const functionConfigRaw = typeof toolDataRaw.functionConfig === 'string' ? toolDataRaw.functionConfig : initialFunctionConfig;
  const integrationIdRaw = typeof toolDataRaw.integrationId === 'string' ? toolDataRaw.integrationId : '';
  const integrationNameRaw = typeof toolDataRaw.integrationName === 'string' ? toolDataRaw.integrationName : '';
  const integrationLogoRaw = typeof toolDataRaw.integrationLogo === 'string' ? toolDataRaw.integrationLogo : '';
  const systemToolKeyRaw = typeof toolDataRaw.systemToolKey === 'string' ? toolDataRaw.systemToolKey : '';
  const executionModeRaw = typeof toolDataRaw.executionMode === 'string' && toolDataRaw.executionMode.toLowerCase() === 'async' ? 'async' : 'sync';
  const toolIdValue = safeRow.toolId !== undefined ? safeRow.toolId : safeRow.toolid;
  const toolGuidValue = safeRow.toolGuid !== undefined ? safeRow.toolGuid : safeRow.toolguid;
  const organizationIdValue = safeRow.organizationId !== undefined ? safeRow.organizationId : safeRow.organizationid;
  const toolNameValue = safeRow.toolName !== undefined ? safeRow.toolName : safeRow.toolname;
  const createdAtValue = safeRow.createdAt !== undefined ? safeRow.createdAt : safeRow.createdat;
  const updatedAtValue = safeRow.updatedAt !== undefined ? safeRow.updatedAt : safeRow.updatedat;
  return {
    toolId: toolIdValue,
    toolGuid: toolGuidValue,
    organizationId: organizationIdValue,
    toolName: toolNameValue,
    createdAt: createdAtValue,
    updatedAt: updatedAtValue,
    toolData: {
      requiredParams: requiredParamsRaw,
      description: descriptionRaw,
      example: exampleRaw,
      functionConfig: functionConfigRaw,
      integrationId: integrationIdRaw,
      integrationName: integrationNameRaw,
      integrationLogo: integrationLogoRaw,
      systemToolKey: systemToolKeyRaw,
      executionMode: executionModeRaw
    }
  };
};

const filteredTools = computed(() => {
  const q = typeof searchValue.value === 'string' ? searchValue.value.trim().toLowerCase() : '';
  if (q === '') {
    return tools.value;
  }
  return tools.value.filter((tool) => {
    const name = typeof tool?.toolName === 'string' ? tool.toolName.toLowerCase() : '';
    const desc = typeof tool?.toolData?.description === 'string' ? tool.toolData.description.toLowerCase() : '';
    return name.includes(q) === true || desc.includes(q) === true;
  });
});

const groupedTools = computed(() => {
  const groups = new Map();
  const rows = filteredTools.value;

  for (let i = 0; i < rows.length; i = i + 1) {
    const tool = rows[i];
    const meta = getToolCollectionMeta(tool);
    const existing = groups.get(meta.id);
    if (existing === undefined) {
      groups.set(meta.id, {
        collectionId: meta.id,
        name: meta.name,
        logo: meta.logo,
        items: [tool]
      });
    } else {
      existing.items.push(tool);
    }
  }

  const list = Array.from(groups.values());
  list.sort((a, b) => {
    const rank = (collectionId) => {
      if (collectionId === 'custom') return 0; // custom tools
      if (collectionId === 'aiTools') return 2; // system tools
      return 1; // added tools (integrations)
    };

    const rankA = rank(a.collectionId);
    const rankB = rank(b.collectionId);
    if (rankA !== rankB) {
      return rankA - rankB;
    }

    return a.name.localeCompare(b.name);
  });
  return list;
});

function getToolLogo(tool) {
  return getToolLogoUrl(tool);
}

function getCollectionIcon(collectionId) {
  if (collectionId === 'custom') {
    return 'tabler:tools';
  }
  if (collectionId === 'aiTools') {
    return 'tabler:sparkles';
  }
  return '';
}

function getToolIcon(tool) {
  const meta = getToolCollectionMeta(tool);
  return getCollectionIcon(meta.id);
}

function isBuiltInTool(tool) {
  const meta = getToolCollectionMeta(tool);
  return meta.id === 'aiTools';
}

function isSystemToolRow(tool) {
  const systemKey = tool !== null && tool !== undefined && tool.toolData !== null && tool.toolData !== undefined && typeof tool.toolData.systemToolKey === 'string'
    ? tool.toolData.systemToolKey.trim()
    : '';
  if (systemKey !== '') {
    return true;
  }
  return tool !== null && tool !== undefined && (tool.organizationId === null || tool.organizationId === undefined);
}

const formatToolDescription = (row) => {
  const desc = row !== null && row !== undefined && row.toolData !== null && row.toolData !== undefined && typeof row.toolData.description === 'string' ? row.toolData.description : '';
  return desc.length > 60 ? desc.slice(0, 60) + '...' : desc;
};

const formatRequiredParams = (row) => {
  const params = row !== null && row !== undefined && row.toolData !== null && row.toolData !== undefined && Array.isArray(row.toolData.requiredParams) === true ? row.toolData.requiredParams : [];
  return params.join(', ');
};

const formatExecutionMode = (row) => {
  const executionMode = row !== null && row !== undefined && row.toolData !== null && row.toolData !== undefined && row.toolData.executionMode === 'async' ? 'async' : 'sync';
  return executionMode === 'async' ? t('aiTools.executionModeAsync') : t('aiTools.executionModeSync');
};

const formatDateTime = (value) => {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime()) === true) {
      return '';
    }
    return d.toLocaleString();
  } catch {
    return '';
  }
};

const buildFakeValuesTemplate = (requiredParams) => {
  const params = Array.isArray(requiredParams) === true ? requiredParams : [];
  const template = {};
  for (let i = 0; i < params.length; i = i + 1) {
    const paramName = typeof params[i] === 'string' ? params[i].trim() : '';
    if (paramName.length > 0) {
      template[paramName] = `fake_${paramName}`;
    }
  }
  return template;
};

const resetRunState = () => {
  isRunningTool.value = false;
  runResult.value = '';
  runError.value = '';
  runParamsPreview.value = '';
  runStatus.value = '';
};

const resetAiPromptState = () => {
  aiPromptInput.value = '';
  isGeneratingToolWithAi.value = false;
  aiPromptStatus.value = '';
};

const fillFakeValuesTemplate = () => {
  const requiredParams = currentTool.value !== null
    && currentTool.value !== undefined
    && currentTool.value.toolData !== null
    && currentTool.value.toolData !== undefined
    && Array.isArray(currentTool.value.toolData.requiredParams) === true
      ? currentTool.value.toolData.requiredParams
      : [];
  const template = buildFakeValuesTemplate(requiredParams);
  fakeValuesInput.value = JSON.stringify(template, null, 2);
};

const parseFakeValuesInput = () => {
  const raw = typeof fakeValuesInput.value === 'string' ? fakeValuesInput.value.trim() : '';
  if (raw.length === 0) {
    return {};
  }
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw new Error(t('aiTools.toasts.fakeValuesInvalidJson'));
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed) === true) {
    throw new Error(t('aiTools.toasts.fakeValuesMustBeObject'));
  }
  return parsed;
};

const formatRunData = (value) => {
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatRunStatus = (statusCode) => {
  if (Number.isFinite(statusCode) === true) {
    return `HTTP ${statusCode}`;
  }
  return '';
};

const extractRunErrorDetails = (error) => {
  const response = error !== undefined && error !== null && error.response !== undefined && error.response !== null
    ? error.response
    : null;
  const statusCode = response !== null && Number.isFinite(response.status) === true
    ? response.status
    : null;
  const responseData = response !== null && response.data !== undefined
    ? response.data
    : null;
  const responseObjectError = responseData !== null
    && responseData !== undefined
    && typeof responseData === 'object'
    && typeof responseData.error === 'string'
      ? responseData.error
      : null;
  const responseStringError = typeof responseData === 'string'
    ? responseData
    : null;
  const fallbackMessage = error !== undefined && error !== null && typeof error.message === 'string'
    ? error.message
    : t('aiTools.toasts.runError');
  const message = responseObjectError !== null && responseObjectError.trim().length > 0
    ? responseObjectError
    : (responseStringError !== null && responseStringError.trim().length > 0 ? responseStringError : fallbackMessage);
  return {
    statusCode,
    responseData,
    message
  };
};

const loadTools = async () => {
  isLoading.value = true;
  try {
    const res = await axios.get(`${apiEndpoint.value}/api/tools/getAllAITools`, { params: { includeSystemTools: true } });
    if (res !== undefined && res.data !== undefined && Array.isArray(res.data) === true) {
      tools.value = res.data.map(normalizeToolRow);
    }
  } catch (error) {
    console.error('Error loading tools:', error);
    toast.error(t('aiTools.toasts.loadError'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  currentTool.value = {
    toolId: null,
    toolGuid: null,
    organizationId: 0,
    toolName: '',
    toolData: {
      functionConfig: initialFunctionConfig,
      description: '',
      example: '',
      executionMode: 'sync',
      requiredParams: []
    }
  };
  validationError.value = null;
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.setValue(initialFunctionConfig);
  }
  isEditMode.value = false;
  autoFillMissingFakeValues.value = true;
  fillFakeValuesTemplate();
  resetRunState();
  resetAiPromptState();
};

const addRequiredParam = () => {
  currentTool.value.toolData.requiredParams.push('');
};

const removeRequiredParam = (index) => {
  currentTool.value.toolData.requiredParams.splice(index, 1);
};

const validateFunction = async () => {
  try {
    const functionBodyRaw = currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== undefined && currentTool.value.toolData !== null ? currentTool.value.toolData.functionConfig : '';
    const functionBody = typeof functionBodyRaw === 'string' ? functionBodyRaw : '';
    const requiredParams = currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== undefined && currentTool.value.toolData !== null && Array.isArray(currentTool.value.toolData.requiredParams) === true ? currentTool.value.toolData.requiredParams : [];

    const unusedParams = requiredParams.filter(param => param.trim().length > 0 && functionBody.indexOf(param.trim()) < 0);
    if (unusedParams.length > 0) {
      throw new Error(`The following parameters are defined but not used: ${unusedParams.join(', ')}`);
    }

    const cfg = { withCredentials: true };
    const res = await axios.post(`${apiEndpoint.value}/api/tools/validateFunctionConfig`, { functionConfig: functionBody }, cfg);
    const valid = res !== undefined && res.data !== undefined && res.data.valid === true;
    if (valid === true) {
      validationError.value = null;
      toast.success(t('aiTools.toasts.validationSuccess'));
      return;
    }
    const serverError = res !== undefined && res.data !== undefined && typeof res.data.error === 'string' ? res.data.error : 'Function validation failed';
    throw new Error(serverError);
  } catch (error) {
    const msg = error !== undefined && error !== null && typeof error.message === 'string' ? error.message : 'Function validation failed';
    validationError.value = msg;
    toast.error(t('aiTools.toasts.validationError', { error: msg }));
  }
};

const runToolWithFakeValues = async () => {
  try {
    runError.value = '';
    runResult.value = '';
    runParamsPreview.value = '';
    runStatus.value = '';

    const toolIdRaw = currentTool.value !== null && currentTool.value !== undefined
      ? currentTool.value.toolId
      : null;
    const toolId = typeof toolIdRaw === 'number'
      ? toolIdRaw
      : (typeof toolIdRaw === 'string' ? Number.parseInt(toolIdRaw, 10) : NaN);
    if (Number.isFinite(toolId) !== true || toolId <= 0) {
      toast.error(t('aiTools.toasts.runRequiresSavedTool'));
      return;
    }

    const fakeValues = parseFakeValuesInput();
    isRunningTool.value = true;

    const payload = {
      toolId,
      values: fakeValues,
      autoFillMissing: autoFillMissingFakeValues.value === true
    };
    const res = await axios.post(`${apiEndpoint.value}/api/tools/runTool`, payload);
    const responseData = res !== undefined && res.data !== undefined && res.data !== null ? res.data : {};
    const outputText = formatRunData(responseData.output);
    const paramsUsedText = formatRunData(responseData.paramsUsed);
    const autoFilled = Array.isArray(responseData.autoFilledParams) === true ? responseData.autoFilledParams : [];
    const httpStatusText = formatRunStatus(res.status);
    runResult.value = outputText;
    runStatus.value = httpStatusText;
    runParamsPreview.value = paramsUsedText + (autoFilled.length > 0 ? `\n\nAuto-filled: ${autoFilled.join(', ')}` : '');
    toast.success(t('aiTools.toasts.runSuccess'));
  } catch (error) {
    const errorDetails = extractRunErrorDetails(error);
    const responseText = errorDetails.responseData !== null && errorDetails.responseData !== undefined
      ? formatRunData(errorDetails.responseData)
      : '';
    const message = errorDetails.message;
    runStatus.value = formatRunStatus(errorDetails.statusCode);
    runError.value = message;
    runResult.value = responseText;
    toast.error(t('aiTools.toasts.runError'));
  } finally {
    isRunningTool.value = false;
  }
};

const generateToolFromPrompt = async () => {
  try {
    const promptText = typeof aiPromptInput.value === 'string' ? aiPromptInput.value.trim() : '';
    if (promptText.length === 0) {
      toast.error(t('aiTools.toasts.aiPromptRequired'));
      return;
    }

    isGeneratingToolWithAi.value = true;
    aiPromptStatus.value = '';

    const payload = {
      prompt: promptText,
      currentTool: {
        toolName: currentTool.value !== null && currentTool.value !== undefined && typeof currentTool.value.toolName === 'string'
          ? currentTool.value.toolName
          : '',
        toolData: {
          functionConfig: currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== null && currentTool.value.toolData !== undefined && typeof currentTool.value.toolData.functionConfig === 'string'
            ? currentTool.value.toolData.functionConfig
            : '',
          description: currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== null && currentTool.value.toolData !== undefined && typeof currentTool.value.toolData.description === 'string'
            ? currentTool.value.toolData.description
            : '',
          example: currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== null && currentTool.value.toolData !== undefined && typeof currentTool.value.toolData.example === 'string'
            ? currentTool.value.toolData.example
            : '',
          executionMode: currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== null && currentTool.value.toolData !== undefined && currentTool.value.toolData.executionMode === 'async'
            ? 'async'
            : 'sync',
          requiredParams: currentTool.value !== null && currentTool.value !== undefined && currentTool.value.toolData !== null && currentTool.value.toolData !== undefined && Array.isArray(currentTool.value.toolData.requiredParams) === true
            ? currentTool.value.toolData.requiredParams
            : []
        }
      }
    };

    const res = await axios.post(`${apiEndpoint.value}/api/tools/generateToolFromPrompt`, payload);
    const responseData = res !== undefined && res.data !== undefined && res.data !== null ? res.data : {};
    const suggestion = responseData.suggestion !== null && responseData.suggestion !== undefined && typeof responseData.suggestion === 'object'
      ? responseData.suggestion
      : null;
    if (suggestion === null) {
      throw new Error(t('aiTools.toasts.aiGenerateError'));
    }

    const suggestionToolName = typeof suggestion.toolName === 'string' ? sanitizeToolName(suggestion.toolName.trim()) : '';
    if (suggestionToolName.length > 0) {
      currentTool.value.toolName = suggestionToolName;
    }

    const suggestionToolData = suggestion.toolData !== null && suggestion.toolData !== undefined && typeof suggestion.toolData === 'object'
      ? suggestion.toolData
      : {};

    if (typeof suggestionToolData.description === 'string') {
      currentTool.value.toolData.description = suggestionToolData.description;
    }
    if (typeof suggestionToolData.example === 'string') {
      currentTool.value.toolData.example = suggestionToolData.example;
    }

    if (Array.isArray(suggestionToolData.requiredParams) === true) {
      currentTool.value.toolData.requiredParams = suggestionToolData.requiredParams
        .map((param) => typeof param === 'string' ? param.trim() : '')
        .filter((param) => param.length > 0);
    }

    const suggestionFunctionConfig = typeof suggestionToolData.functionConfig === 'string' ? suggestionToolData.functionConfig.trim() : '';
    if (suggestionFunctionConfig.length > 0) {
      currentTool.value.toolData.functionConfig = suggestionFunctionConfig;
      if (monacoEditorInstance !== null) {
        monacoEditorInstance.setValue(suggestionFunctionConfig);
      }
    }

    const modelId = typeof responseData.modelId === 'string' ? responseData.modelId : '';
    const hasParseWarning = responseData.parseWarning === true;
    aiPromptStatus.value = hasParseWarning === true
      ? t('aiTools.aiPromptAppliedWithWarning', { modelId })
      : t('aiTools.aiPromptApplied', { modelId });
    toast.success(t('aiTools.toasts.aiGenerateSuccess'));
  } catch (error) {
    const serverMessage = error !== undefined && error !== null && error.response !== undefined && error.response !== null && error.response.data !== undefined && error.response.data !== null && typeof error.response.data.error === 'string'
      ? error.response.data.error
      : null;
    const message = serverMessage !== null
      ? serverMessage
      : (error !== undefined && error !== null && typeof error.message === 'string' ? error.message : t('aiTools.toasts.aiGenerateError'));
    aiPromptStatus.value = message;
    toast.error(t('aiTools.toasts.aiGenerateError'));
  } finally {
    isGeneratingToolWithAi.value = false;
  }
};

const saveTool = async () => {
  try {
    const sanitizedToolName = sanitizeToolName(currentTool.value.toolName);
    currentTool.value.toolName = sanitizedToolName;

    if (sanitizedToolName.length === 0) {
      toast.error(t('aiTools.toasts.nameRequired'));
      return;
    }

    if (currentTool.value.toolData.functionConfig === undefined || currentTool.value.toolData.functionConfig.trim().length === 0) {
      toast.error(t('aiTools.toasts.configRequired'));
      return;
    }

    const toolData = {
      toolName: sanitizedToolName,
      toolData: {
        functionConfig: currentTool.value.toolData.functionConfig.trim(),
        description: isDefinedAndNotEmpty(currentTool.value.toolData.description) != false ? currentTool.value.toolData.description.trim() : '',
        example: isDefinedAndNotEmpty(currentTool.value.toolData.example) != false ? currentTool.value.toolData.example.trim() : '',
        executionMode: currentTool.value.toolData.executionMode === 'async' ? 'async' : 'sync',
        requiredParams: Array.isArray(currentTool.value.toolData.requiredParams) != false ? 
          currentTool.value.toolData.requiredParams.filter(param => param.trim().length > 0) : []
      }
    };

    if (isEditMode.value === true) {
      await axios.put(`${apiEndpoint.value}/api/tools/updateTool/${currentTool.value.toolId}`, toolData);
      toast.success(t('aiTools.toasts.updateSuccess'));
    } else {
      await axios.post(`${apiEndpoint.value}/api/tools/createTool`, toolData);
      toast.success(t('aiTools.toasts.createSuccess'));
    }

    showCreateModal.value = false;
    loadTools();
  } catch (error) {
    console.error('Error saving tool:', error);
    const serverMsg = error.response?.data?.error || error.response?.data?.message;
    toast.error(serverMsg !== undefined && serverMsg !== null ? serverMsg : t('aiTools.toasts.saveError'));
  }
};

const handleEditClick = (toolId) => {
  console.log('Editing tool:', toolId); 
  const tool = tools.value.find(t => t.toolId === toolId);
  if (tool !== undefined) {
    currentTool.value = {
      toolId: tool.toolId,
      toolGuid: tool.toolGuid,
      organizationId: tool.organizationId,
      toolName: tool.toolName,
      toolData: {
        functionConfig: isDefinedAndNotEmpty(tool.toolData.functionConfig) != false ? tool.toolData.functionConfig : initialFunctionConfig,
        description: isDefinedAndNotEmpty(tool.toolData.description) != false ? tool.toolData.description : '',
        example: isDefinedAndNotEmpty(tool.toolData.example) != false ? tool.toolData.example : '',
        executionMode: tool.toolData.executionMode === 'async' ? 'async' : 'sync',
        requiredParams: Array.isArray(tool.toolData.requiredParams) != false ? [...tool.toolData.requiredParams] : []
      }
    };
    isEditMode.value = true;
    showCreateModal.value = true;
    if (monacoEditorInstance !== null) {
        monacoEditorInstance.setValue(currentTool.value.toolData.functionConfig);
    }
    autoFillMissingFakeValues.value = true;
    fillFakeValuesTemplate();
    resetRunState();
    resetAiPromptState();
  }
};

const handleDeleteClick = (toolId) => {
    console.log('Deleting tool:', toolId);
  if (toolId > 0) {
    selectedToolId.value = toolId;
    isDeleteModalOpen.value = true;
  }
};

const deleteToolConfirmed = async () => {
  try {
    await axios.delete(`${apiEndpoint.value}/api/tools/deleteAITool/${selectedToolId.value}`);
    toast.success(t('aiTools.toasts.deleteSuccess'));
    loadTools();
    isDeleteModalOpen.value = false;
    selectedToolId.value = null;
  } catch (error) {
    console.error('Error deleting tool:', error);
    toast.error(t('aiTools.toasts.deleteError'));
  }
};

const handleResize = () => {
  if (resizeTimeout > 0) {
    clearTimeout(resizeTimeout);
  }
  
  resizeTimeout = setTimeout(() => {
    if (monacoEditorInstance !== null && monacoContainer.value !== null && monacoContainer.value.offsetParent !== null) {
       monacoEditorInstance.layout();
    }
  }, 100);
};

const handleResizeError = (event) => {
  if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
    event.stopImmediatePropagation();
  }
};

onMounted(() => {
  loadTools();
  loadStyle(`
    .vue3-easy-data-table {
      height: 95%;
    }
  `);
  window.addEventListener('resize', handleResize);
  window.addEventListener('error', handleResizeError);
});

function loadStyle(cssString) {
  const style = document.createElement('style');
  style.type = 'text/css';

  style.appendChild(document.createTextNode(cssString));

  document.head.appendChild(style);
}

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('error', handleResizeError);
  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
  }
  destroyMonaco();
});

watch(() => showCreateModal.value, async (newVal) => {
  if (newVal === true) {
    await nextTick();
    initMonaco();
  } else {
    destroyMonaco();
  }
});

const handleCreateClick = () => {
  resetForm();
  showCreateModal.value = true;
};

const closeModal = () => {
  showCreateModal.value = false;
  resetForm();
};

let isMonacoLoading = false;
const initMonaco = async () => {
  if (monacoContainer.value !== null && monacoEditorInstance === null && isMonacoLoading === false) {
    isMonacoLoading = true;
    try {
      const monaco = await loader.init();
      monacoEditorInstance = monaco.editor.create(monacoContainer.value, {
        value: currentTool.value.toolData.functionConfig,
        language: 'javascript',
        theme: 'vs-dark',
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
      });

      monacoEditorInstance.onDidChangeModelContent(() => {
        if (monacoEditorInstance !== null) {
          currentTool.value.toolData.functionConfig = monacoEditorInstance.getValue();
        }
      });
      console.log("Monaco editor initialized for AITools.");
    } catch (error) {
      console.error("Failed to initialize Monaco editor for AITools:", error);
      toast.error(t('aiTools.toasts.editorLoadError'));
    } finally {
       isMonacoLoading = false;
    }
  } else if (monacoEditorInstance !== null && monacoContainer.value !== null) {
    monacoEditorInstance.layout();
    const editorValue = monacoEditorInstance.getValue();
    if (editorValue !== currentTool.value.toolData.functionConfig) {
      monacoEditorInstance.setValue(currentTool.value.toolData.functionConfig);
    }
  }
};

const destroyMonaco = () => {
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.dispose();
    monacoEditorInstance = null;
    console.log("Monaco editor disposed for AITools.");
  }
};
</script>

<style>
.badge-error, .btn-error, .badge-success, .btn-success {
  color: #fff;
}
table {
  font-family: "Heebo", sans-serif !important;
}
.scroll-content {
  height: 100%;
}
.vue3-easy-data-table {
  height: 95%;
}

#webpack-dev-server-client-overlay[data-message*="ResizeObserver loop completed with undelivered notifications"] {
  display: none !important;
}



.modal-box {
 
  padding-top: 1rem;
  padding-bottom: 1rem;
}


.modal-box > .space-y-4 {
  overflow-y: auto;
  max-height: calc(80vh - 10rem); 
}

.sticky {
  position: sticky;
  background-color: inherit; 
}

.monaco-editor-container {
  direction: ltr !important;
  unicode-bidi: embed !important;
  height: 300px; 
  border: 1px solid #e5e7eb; 
  border-radius: 8px;   
  overflow: hidden; 
  margin-bottom: 1rem;
  width: 100%;
  position: relative; 
}
</style> 